import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowParameterValueErrorType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { buildJoinColumnList, buildJoinOutputSchema, tempTableName, validateAtLeastOneColumnSelected } from '../utils'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['covers', 'ST_COVERS']
])

class SpatialJoinSF extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialjoin',
        group: 'Joins',
        title: 'Spatial Join',
        description: `
    This component performs a spatial join between two tables, based on a spatial predicate.
    `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        },
        {
          name: 'maintablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Main table columns',
          parent: 'maintable',
          optional: true
        },
        {
          name: 'maintablejoincolumn',
          type: WorkflowComponentParamType.Column,
          parent: 'maintable',
          title: 'Main table join column',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'secondarytablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Secondary table columns',
          parent: 'secondarytable',
          optional: true
        },
        {
          name: 'secondarytablejoincolumn',
          type: WorkflowComponentParamType.Column,
          parent: 'secondarytable',
          title: 'Secondary table join column',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'jointype',
          type: WorkflowComponentParamType.Selection,
          title: 'Join type',
          default: 'inner',
          options: ['inner', 'left', 'right', 'full']
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    codeType,
    schemas,
    componentName
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)

    const mainTable = preparedParams.maintable as string
    const secondaryTable = preparedParams.secondarytable as string
    const mainTableColumns = preparedParams.maintablecolumns as string
    const secondaryTableColumns = preparedParams.secondarytablecolumns as string
    const mainTableJoinColumn = preparedParams.maintablejoincolumn as string
    const secondaryTableJoinColumn = preparedParams.secondarytablejoincolumn as string
    const joinType = preparedParams.jointype as string
    const predicate = preparedParams.predicate as string

    const cols = buildJoinColumnList(mainTableColumns, secondaryTableColumns, '_main', '_joined')

    const select = `
      SELECT
        ${cols.join(', ')}
      FROM
        ${connection.quoteName(mainTable)} AS _main
      ${joinType.toUpperCase()} JOIN
        ${connection.quoteName(secondaryTable)} AS _joined
      ON
        ${predicates.get(predicate)}(
          _main.${mainTableJoinColumn},
          _joined.${secondaryTableJoinColumn}
        )`

    const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    switch (connection.type) {
      case Provider.Snowflake: {
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(this.metadata.name, preparedParams.secondarytable)}`
          storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.secondarytable as string).split('.')
        }
        query = dedent`DECLARE alias STRING;
                BEGIN
                  ${
                    storedProc
                      ? dedent`
                          CREATE TABLE ${storedProcTable} AS
                          SELECT * FROM ${connection.quoteName(preparedParams.secondarytable as string)}
                          WHERE 1=0;`
                      : ''
                  }
                  LET res resultset := (EXECUTE IMMEDIATE '
                    with __alias AS(
                    SELECT CONCAT(''_joined.'', column_name, '' AS '', column_name, ''_joined'') col_alias
                    FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                    WHERE table_name = ''${tablename.toUpperCase()}'' and
                    table_schema = ''${schemaname.toUpperCase()}'' and
                    table_catalog = ''${datasetname.toUpperCase()}''
                    )
                    SELECT LISTAGG(col_alias, '', '') aliases
                    FROM __alias
                  ');
                  LET cur_res CURSOR FOR res;
                  OPEN cur_res;
                  FETCH cur_res INTO alias;
                  ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                  EXECUTE IMMEDIATE
                    REPLACE(
                      'BEGIN
                        ${createTable}
                      END;',
                      '%s',
                      alias
                    );
                END;`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })

    // Check if at least one column is selected from either table
    const columnSelectionError = validateAtLeastOneColumnSelected(
      resolvedParamValues.maintablecolumns as string | null,
      resolvedParamValues.secondarytablecolumns as string | null,
      'maintablecolumns'
    )
    if (columnSelectionError) {
      problems.push(columnSelectionError)
    }

    return problems
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getVersion(provider: Provider): string {
    return '2'
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return buildJoinOutputSchema(
      columns.maintable,
      columns.secondarytable,
      params.maintablecolumns as string,
      params.secondarytablecolumns as string
    )
  }
  supportedProviders(): Provider[] {
    return [Provider.Snowflake]
  }
}

export { SpatialJoinSF }
